import React, { useState, useEffect } from "react";
import { baseurl } from "../../baseurl";
import axios from "axios";
import { FiPhone, FiMail, FiMapPin, FiHash, FiCalendar, FiFileText, FiArrowRight } from "react-icons/fi";

/* ─────────────────────────────────────────────
   INLINE CSS — "Aurora" design
   Light white paper + soft teal/mint/peach gradient header
   + pastel section bands + warm gold totals
───────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&display=swap');

.it7__wrap * { box-sizing: border-box; margin: 0; padding: 0; }

.it7__wrap {
  font-family: 'Outfit', sans-serif;
  background: #dde8e8;
  min-height: 100vh;
 
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

/* ── A4 paper ── */
.it7__paper {
  width: 210mm;
  min-height: 297mm;
  background: #ffffff;
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(10,85,86,0.18), 0 2px 8px rgba(10,85,86,0.10);
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

.it7__inner { display: flex; flex-direction: column; flex: 1; }

/* ══════════════════════════════════════
   HEADER — soft gradient sash
══════════════════════════════════════ */
.it7__header {
  background: linear-gradient(120deg, #e8f7f7 0%, #d4f0ed 35%, #e8f3f8 65%, #f0ede4 100%);
  padding: 36px 44px 30px;
  border-bottom: 2px solid rgba(10,85,86,0.08);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  position: relative;
  overflow: hidden;
}

/* soft decorative arc top-right */
.it7__header::after {
  content: '';
  position: absolute;
  width: 280px; height: 280px;
  border-radius: 50%;
  border: 40px solid rgba(10,85,86,0.04);
  top: -100px; right: -80px;
  pointer-events: none;
}

/* brand */
.it7__brand { display: flex; flex-direction: column; gap: 0; position: relative; z-index: 1; }
.it7__brand-mark {
  display: flex; align-items: center; gap: 14px; margin-bottom: 12px;
}
.it7__init-box {
  width: 52px; height: 52px; border-radius: 16px; flex-shrink: 0;
  background: linear-gradient(135deg, #0a5556, #14b8a6);
  display: flex; align-items: center; justify-content: center;
  font-size: 22px; font-weight: 900; color: #ffffff;
  box-shadow: 0 6px 20px rgba(10,85,86,0.28);
}
.it7__company-name {
  font-size: 22px; font-weight: 800; color: #0a3d3e;
  letter-spacing: -0.02em; line-height: 1.15;
}
.it7__seller-row {
  display: flex; align-items: center; gap: 7px;
  font-size: 12px; color: #6a9090; margin-bottom: 5px;
}
.it7__seller-row svg { color: #14b8a6; flex-shrink: 0; }
.it7__gstin-badge {
  display: inline-flex; align-items: center;
  padding: 4px 11px; border-radius: 8px; margin-top: 4px;
  background: rgba(10,85,86,0.08); border: 1px solid rgba(10,85,86,0.18);
  font-size: 10px; font-weight: 700; color: #0a5556;
  letter-spacing: 0.05em;
}

/* invoice title right */
.it7__title-block { text-align: right; position: relative; z-index: 1; }
.it7__inv-word {
  font-size: 44px; font-weight: 900; color: rgba(10,85,86,0.09);
  letter-spacing: 0.14em; text-transform: uppercase; line-height: 1; margin-bottom: 18px;
}
.it7__meta-items { display: flex; flex-direction: column; gap: 9px; }
.it7__meta-item {
  display: flex; flex-direction: column; gap: 1px; align-items: flex-end;
}
.it7__meta-lbl {
  font-size: 9px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase;
  color: #9ababa;
}
.it7__meta-val {
  font-size: 13.5px; font-weight: 800; color: #0f2a2a;
}

/* status */
.it7__status {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 12px; border-radius: 50px;
  font-size: 11px; font-weight: 700;
}
.it7__status--paid    { background: #d1fae5; color: #065f46; border: 1px solid #6ee7b7; }
.it7__status--unpaid  { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }
.it7__status--partial { background: #fef3c7; color: #92400e; border: 1px solid #fcd34d; }
.it7__status--pending { background: #f0f9ff; color: #075985; border: 1px solid #7dd3fc; }
.it7__status-dot { width: 6px; height: 6px; border-radius: 50%; }

/* ── TEAL ACCENT LINE ── */
.it7__accent {
  height: 3px;
  background: linear-gradient(90deg, #0a5556 0%, #14b8a6 50%, #FFD166 100%);
}

/* ══════════════════════════════════════
   BILLING CARDS (2 col)
══════════════════════════════════════ */
.it7__billing {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 14px;
  padding: 22px 44px;
  background: #f9fefe;
  border-bottom: 1px solid rgba(10,85,86,0.08);
}
.it7__bill-card {
  background: #ffffff;
  border: 1px solid rgba(10,85,86,0.10);
  border-radius: 14px; padding: 16px 18px;
  box-shadow: 0 2px 8px rgba(10,85,86,0.05);
}
.it7__card-lbl {
  font-size: 9px; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase;
  color: #ffffff; background: #0a5556;
  display: inline-block; padding: 3px 9px; border-radius: 4px; margin-bottom: 12px;
}
.it7__client-name {
  font-size: 15px; font-weight: 800; color: #0f2a2a; margin-bottom: 9px; line-height: 1.2;
}
.it7__client-row {
  display: flex; align-items: flex-start; gap: 7px;
  font-size: 12px; color: #5a8080; margin-bottom: 5px; line-height: 1.4;
}
.it7__client-row svg { color: #14b8a6; flex-shrink: 0; margin-top: 1px; }
.it7__gstin-sm {
  display: inline-block; padding: 2px 8px; border-radius: 5px; margin-top: 4px;
  font-size: 10.5px; font-weight: 700;
  background: rgba(10,85,86,0.07); color: #0a5556;
  border: 1px solid rgba(10,85,86,0.16);
}

/* detail rows */
.it7__det-row {
  display: flex; align-items: center; gap: 10px; padding: 9px 0;
  border-bottom: 1px solid rgba(10,85,86,0.06);
}
.it7__det-row:last-child { border-bottom: none; padding-bottom: 0; }
.it7__det-icon {
  width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
  background: rgba(10,85,86,0.07); border: 1px solid rgba(10,85,86,0.12);
  display: flex; align-items: center; justify-content: center;
  color: #0a5556;
}
.it7__det-lbl { font-size: 9.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.09em; color: #9ababa; margin-bottom: 1px; }
.it7__det-val { font-size: 13px; font-weight: 700; color: #0f2a2a; }

/* ══════════════════════════════════════
   TABLE
══════════════════════════════════════ */
.it7__table-wrap { padding: 22px 44px; flex: 1; }
.it7__table-lbl {
  font-size: 9px; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase;
  color: #9ababa; margin-bottom: 12px;
}
.it7__table { width: 100%; border-collapse: collapse; border: 1px solid rgba(10,85,86,0.08); border-radius: 12px; overflow: hidden; }

.it7__table thead tr {
  background: linear-gradient(90deg, #0a5556, #0d7070);
}
.it7__table thead th {
  font-size: 9.5px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
  color: rgba(255,255,255,0.80); padding: 12px 14px; text-align: left;
}
.it7__table thead th:first-child { padding-left: 18px; }
.it7__table thead th.th-r { text-align: right; padding-right: 18px; }
.it7__table thead th.th-c { text-align: center; }

.it7__table tbody tr {
  border-bottom: 1px solid rgba(10,85,86,0.06);
  transition: background 0.12s;
}
.it7__table tbody tr:nth-child(even) { background: #f7fefe; }
.it7__table tbody tr:last-child { border-bottom: none; }
.it7__table tbody tr:hover { background: #eef7f7 !important; }
.it7__table tbody td { padding: 13px 14px; font-size: 13px; color: #0f2a2a; vertical-align: middle; }
.it7__table tbody td:first-child { padding-left: 18px; }
.it7__table tbody td.td-c { text-align: center; font-weight: 700; color: #0f2a2a; }
.it7__table tbody td.td-r { text-align: right; padding-right: 18px; color: #5a8080; font-weight: 500; }
.it7__table tbody td.td-total { text-align: right; padding-right: 18px; }

.it7__item-name { font-size: 13px; font-weight: 700; color: #0f2a2a; margin-bottom: 4px; }
.it7__hsn-pill {
  display: inline-block; padding: 2px 8px; border-radius: 50px;
  font-size: 10px; font-weight: 600;
  background: rgba(10,85,86,0.07); color: #0a5556;
  border: 1px solid rgba(10,85,86,0.14); margin-right: 5px;
}
.it7__tax-pill {
  display: inline-block; padding: 2px 8px; border-radius: 50px;
  font-size: 10px; font-weight: 600;
  background: rgba(245,158,11,0.10); color: #92400e;
  border: 1px solid rgba(245,158,11,0.25);
}
.it7__amount-val {
  font-size: 14px; font-weight: 800; color: #0a5556;
}

/* ══════════════════════════════════════
   FOOTER
══════════════════════════════════════ */
.it7__footer {
  display: flex; align-items: stretch; gap: 0;
  border-top: 1px solid rgba(10,85,86,0.08);
  margin-top: auto;
}

.it7__notes {
  flex: 1; padding: 20px 44px;
  border-right: 1px solid rgba(10,85,86,0.08);
  background: #fafefe;
}
.it7__notes-lbl {
  font-size: 9px; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase;
  color: #9ababa; margin-bottom: 8px;
}
.it7__notes-txt { font-size: 12.5px; color: #5a8080; line-height: 1.6; }

/* summary */
.it7__summary { width: 250px; flex-shrink: 0; display: flex; flex-direction: column; background: #fff; }
.it7__sum-rows { flex: 1; }
.it7__sum-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px 22px;
  border-bottom: 1px solid rgba(10,85,86,0.06);
}
.it7__sum-lbl { font-size: 12px; color: #5a8080; font-weight: 500; }
.it7__sum-val { font-size: 12.5px; font-weight: 700; color: #0f2a2a; }

/* grand total */
.it7__grand {
  background: linear-gradient(135deg, #0a5556, #0c7172);
  padding: 16px 22px;
  display: flex; justify-content: space-between; align-items: center;
}
.it7__grand-lbl {
  font-size: 9.5px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase;
  color: rgba(255,255,255,0.60);
}
.it7__grand-val {
  font-size: 22px; font-weight: 900; color: #FFD166;
  letter-spacing: -0.01em;
}

/* bottom strip */
.it7__bottom {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 11px 44px;
  background: #f4fafa;
  border-top: 1px solid rgba(10,85,86,0.07);
}
.it7__bottom-txt {
  font-size: 10px; font-weight: 500; color: #9ababa; letter-spacing: 0.06em;
}
.it7__bottom svg { color: #14b8a6; }

/* print */
@page { size: A4 portrait; margin: 0; }
@media print {
  .it7__wrap { background: none !important; padding: 0 !important; display: block !important; }
  .it7__paper { width: 210mm !important; min-height: 297mm !important; box-shadow: none !important; margin: 0 !important; }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
}
@media (max-width: 640px) {
  .it7__billing { grid-template-columns: 1fr; }
  .it7__header { flex-direction: column; }
  .it7__title-block { text-align: left; }
  .it7__footer { flex-direction: column; }
  .it7__summary { width: 100%; }
}
`;

/* helpers */
const num   = (v) => { const n = Number(v); return isNaN(n) ? 0 : n; };
const fmtIN = (v) => num(v).toLocaleString("en-IN", { minimumFractionDigits: 2 });
const init  = (name = "") => name.trim()[0]?.toUpperCase() || "?";

const getStatusInfo = (status = "") => {
  const s = status.toLowerCase();
  if (s === "paid")    return { cls: "it7__status--paid",    dot: "#6ee7b7" };
  if (s === "unpaid")  return { cls: "it7__status--unpaid",  dot: "#fca5a5" };
  if (s === "partial") return { cls: "it7__status--partial", dot: "#fcd34d" };
  return                      { cls: "it7__status--pending", dot: "rgba(255,255,255,0.40)" };
};

/* ═══════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════ */
const InvoiceSeven = ({ invoiceData }) => {
  const [sellerInfo, setSellerInfo] = useState(null);
  const token = localStorage.getItem("auth_token");

  console.log("invoiceData", invoiceData);

  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const response = await axios.get(`${baseurl}getBusinessInfo`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("response", response);
        if (response.data.success) setSellerInfo(response.data.data);
      } catch (error) {
        console.error("Error fetching business info for seller:", error);
      }
    };
    fetchBusinessInfo();
  }, [token]);

  const defaultData = {
    invoice_number: "INV-000",
    invoice_date: new Date().toISOString().split("T")[0],
    due_date: "N/A",
    payment_status: "Pending",
    notes: "Thank you for your business.",
    seller: { name: "N/A", gstin: "N/A", phone: "N/A", state: "N/A", email: "", zipcode: "" },
    customer: { name: "Client Name", email: "client@example.com", gstin: "N/A", phone: "N/A", state: "N/A", address: "Client Address Here" },
    items: [],
    gst_breakdown: { CGST: 0, SGST: 0 },
    subtotal: 0, taxable_amount: 0, grand_total: 0,
  };

  const resolvedSeller =
    invoiceData?.invoice_data?.seller ||
    (sellerInfo ? {
      name: sellerInfo.business_name, gstin: sellerInfo.gst || "N/A",
      phone: sellerInfo.whatsapp_number, state: sellerInfo.state,
      city: sellerInfo.city, zipcode: sellerInfo.zipcode,
      email: sellerInfo.contact_email || sellerInfo.email,
    } : defaultData.seller);

  const resolved = invoiceData?.invoice_data
    ? {
        invoice_number: invoiceData.invoice_number || defaultData.invoice_number,
        invoice_date:   invoiceData.invoice_data.invoiceDate || defaultData.invoice_date,
        due_date:       invoiceData.invoice_data.dueDate,
        payment_status: invoiceData.invoice_data.paymentStatus,
        seller:         resolvedSeller,
        customer:       invoiceData.invoice_data.customer || defaultData.customer,
        items:          invoiceData.invoice_data.items || [],
        gst_breakdown:  invoiceData.invoice_data.gstBreakdown || { CGST: 0, SGST: 0 },
        subtotal:       num(invoiceData.invoice_data.itemSubtotal),
        taxable_amount: num(invoiceData.invoice_data.taxableAmount),
        grand_total:    num(invoiceData.grand_total ?? invoiceData.invoice_data.grandTotal),
        notes:          invoiceData.invoice_data.notes || defaultData.notes,
      }
    : { ...defaultData, seller: resolvedSeller };

  const { invoice_number, invoice_date, payment_status, seller, customer, items, gst_breakdown, subtotal, grand_total, notes } = resolved;
  const si = getStatusInfo(payment_status);

  return (
    <>
      <style>{CSS}</style>
      <div className="it7__wrap">
        <div className="it7__paper">
          <div className="it7__inner">

            {/* HEADER */}
            <header className="it7__header">
              <div className="it7__brand">
                <div className="it7__brand-mark">
                  <div className="it7__init-box">{init(seller.name)}</div>
                  <div className="it7__company-name">{seller.name}</div>
                </div>
                <div>
                  {(seller.city || seller.state) && (
                    <div className="it7__seller-row"><FiMapPin size={11} />{seller.city && `${seller.city}, `}{seller.state} {seller.zipcode}</div>
                  )}
                  {seller.phone && <div className="it7__seller-row"><FiPhone size={11} />{seller.phone}</div>}
                  {seller.email && <div className="it7__seller-row"><FiMail  size={11} />{seller.email}</div>}
                  {seller.gstin !== "N/A" && <div className="it7__gstin-badge">GSTIN: {seller.gstin}</div>}
                </div>
              </div>

              <div className="it7__title-block">
                <div className="it7__inv-word">INVOICE</div>
                <div className="it7__meta-items">
                  <div className="it7__meta-item">
                    <span className="it7__meta-lbl">Invoice Number</span>
                    <span className="it7__meta-val">{invoice_number}</span>
                  </div>
                  <div className="it7__meta-item">
                    <span className="it7__meta-lbl">Issue Date</span>
                    <span className="it7__meta-val">{invoice_date}</span>
                  </div>
                  <div className="it7__meta-item">
                    <span className="it7__meta-lbl">Due Date</span>
                    <span className="it7__meta-val">{resolved.due_date || "N/A"}</span>
                  </div>
                  <div className="it7__meta-item">
                    <span className="it7__meta-lbl">Status</span>
                    <span className="it7__meta-val">
                      <span className={`it7__status ${si.cls}`}>
                        <span className="it7__status-dot" style={{ background: si.dot }} />
                        {payment_status}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </header>

            {/* accent bar */}
            <div className="it7__accent" />

            {/* BILLING */}
            <div className="it7__billing">
              <div className="it7__bill-card">
                <div className="it7__card-lbl">Bill To</div>
                <div className="it7__client-name">{customer.name}</div>
                {customer.address && <div className="it7__client-row"><FiMapPin size={11} />{customer.address}</div>}
                {customer.phone   && <div className="it7__client-row"><FiPhone  size={11} />{customer.phone}</div>}
                {customer.email   && <div className="it7__client-row"><FiMail   size={11} />{customer.email}</div>}
                {customer.gstin !== "N/A" && customer.gstin && (
                  <span className="it7__gstin-sm">GSTIN: {customer.gstin}</span>
                )}
              </div>
              <div className="it7__bill-card">
                <div className="it7__card-lbl">Invoice Details</div>
                <div className="it7__det-row">
                  <div className="it7__det-icon"><FiHash size={12} /></div>
                  <div>
                    <div className="it7__det-lbl">Invoice No</div>
                    <div className="it7__det-val">{invoice_number}</div>
                  </div>
                </div>
                <div className="it7__det-row">
                  <div className="it7__det-icon"><FiCalendar size={12} /></div>
                  <div>
                    <div className="it7__det-lbl">Issue Date</div>
                    <div className="it7__det-val">{invoice_date}</div>
                  </div>
                </div>
                <div className="it7__det-row">
                  <div className="it7__det-icon"><FiCalendar size={12} /></div>
                  <div>
                    <div className="it7__det-lbl">Due Date</div>
                    <div className="it7__det-val">{resolved.due_date || "N/A"}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* TABLE */}
            <div className="it7__table-wrap">
              <div className="it7__table-lbl">Line Items</div>
              <table className="it7__table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th className="th-c">Qty</th>
                    <th className="th-r">Rate</th>
                    <th className="th-r">GST%</th>
                    <th className="th-r">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length > 0 ? items.map((item, i) => {
                    const lineTotal = num(item.qty) * num(item.rate) * (1 + num(item.tax) / 100);
                    return (
                      <tr key={i}>
                        <td>
                          <div className="it7__item-name">{item.description}</div>
                          <span className="it7__hsn-pill">HSN {item.hsn || "—"}</span>
                          <span className="it7__tax-pill">{item.tax}% GST</span>
                        </td>
                        <td className="td-c">{item.qty}</td>
                        <td className="td-r">₹{fmtIN(item.rate)}</td>
                        <td className="td-r">{item.tax}%</td>
                        <td className="td-total"><span className="it7__amount-val">₹{fmtIN(lineTotal)}</span></td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan="5" style={{ textAlign: "center", padding: "30px", color: "rgba(255,255,255,0.25)", fontWeight: 600 }}>No items found</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* FOOTER */}
            <div className="it7__footer">
              <div className="it7__notes">
                <div className="it7__notes-lbl">Notes & Remarks</div>
                <div className="it7__notes-txt">{notes || "Thank you for your business."}</div>
              </div>
              <div className="it7__summary">
                <div className="it7__sum-rows">
                  <div className="it7__sum-row">
                    <span className="it7__sum-lbl">Subtotal</span>
                    <span className="it7__sum-val">₹{fmtIN(subtotal)}</span>
                  </div>
                  <div className="it7__sum-row">
                    <span className="it7__sum-lbl">CGST</span>
                    <span className="it7__sum-val">₹{fmtIN(gst_breakdown.CGST)}</span>
                  </div>
                  <div className="it7__sum-row">
                    <span className="it7__sum-lbl">SGST</span>
                    <span className="it7__sum-val">₹{fmtIN(gst_breakdown.SGST)}</span>
                  </div>
                </div>
                <div className="it7__grand">
                  <span className="it7__grand-lbl">Amount Due</span>
                  <span className="it7__grand-val">₹{fmtIN(grand_total)}</span>
                </div>
              </div>
            </div>

            <div className="it7__bottom">
              <FiFileText size={11} />
              <span className="it7__bottom-txt">This is a computer generated invoice.</span>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default InvoiceSeven;
